import { useCallback, useEffect, useRef, useState } from 'react';

export type AudioCapture = {
  start: (stream: MediaStream) => Promise<void>;
  stop: () => Promise<void>;
  level: number;
  active: boolean;
};

export function useAudioCapture(onPcm: (buf: ArrayBuffer) => void): AudioCapture {
  const onPcmRef = useRef(onPcm);
  onPcmRef.current = onPcm;

  const ctxRef = useRef<AudioContext | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const workletRef = useRef<AudioWorkletNode | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const rafRef = useRef<number | null>(null);
  const startingRef = useRef(false);
  const [level, setLevel] = useState(0);
  const [active, setActive] = useState(false);

  const start = useCallback(async (stream: MediaStream) => {
    if (ctxRef.current || startingRef.current) return;
    startingRef.current = true;
    try {
      const Ctx = window.AudioContext || (window as any).webkitAudioContext;
      const ctx: AudioContext = new Ctx({ latencyHint: 'interactive' });
      ctxRef.current = ctx;
      await ctx.audioWorklet.addModule('/worklets/pcm-worklet.js');
      const source = ctx.createMediaStreamSource(stream);
      sourceRef.current = source;
      const worklet = new AudioWorkletNode(ctx, 'pcm-worklet', {
        numberOfInputs: 1,
        numberOfOutputs: 0,
        processorOptions: { targetRate: 16000, frameMs: 64 },
      });
      worklet.port.onmessage = (e) => {
        onPcmRef.current(e.data as ArrayBuffer);
      };
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      source.connect(worklet);
      workletRef.current = worklet;
      analyserRef.current = analyser;

      const buf = new Uint8Array(analyser.fftSize);
      let lastLvl = 0;
      let lastUpdate = 0;
      const tick = () => {
        analyser.getByteTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) {
          const v = (buf[i]! - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / buf.length);
        const lvl = Math.min(1, rms * 3);
        const smoothed = lastLvl * 0.6 + lvl * 0.4;
        // Throttle React updates to ~10 fps so we don't trigger 60-fps re-renders.
        const now = performance.now();
        if (now - lastUpdate > 100 || Math.abs(smoothed - lastLvl) > 0.05) {
          setLevel(smoothed);
          lastUpdate = now;
        }
        lastLvl = smoothed;
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
      setActive(true);
    } finally {
      startingRef.current = false;
    }
  }, []);

  const stop = useCallback(async () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    try { workletRef.current?.disconnect(); } catch {}
    try { sourceRef.current?.disconnect(); } catch {}
    try { analyserRef.current?.disconnect(); } catch {}
    workletRef.current = null;
    sourceRef.current = null;
    analyserRef.current = null;
    if (ctxRef.current) {
      try { await ctxRef.current.close(); } catch {}
      ctxRef.current = null;
    }
    setActive(false);
    setLevel(0);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      void stop();
    };
  }, [stop]);

  return { start, stop, level, active };
}
